import type { PipelineHealth } from "../../lib/queries.js";
import { Badge, type BadgeTone } from "../ui/Badge.js";
import { Section } from "../ui/Section.js";

/** Long enough to identify the failure, short enough to scan a column. */
const ERROR_CHARS = 120;

const STATUS_LABEL: Record<PipelineHealth["status"], string> = {
  ok: "OK",
  failed: "Failed",
  never: "Never run",
  running: "Running",
  // "Disabled" reads like something broke it. This one was switched off on
  // purpose, and the label is the difference between a row to act on and a
  // row to skip.
  disabled: "Switched off",
};

const STATUS_TONE: Record<PipelineHealth["status"], BadgeTone> = {
  ok: "up",
  failed: "urgent",
  never: "neutral",
  running: "warn",
  disabled: "neutral",
};

/**
 * Every pipeline this build runs, whether or not it has ever run.
 *
 * Never-run and ran-and-wrote-nothing are different states, and a table that
 * shows only the pipelines with rows hides the first one entirely.
 */
export function PipelineTable({ pipelines }: { pipelines: PipelineHealth[] }) {
  return (
    <Section title="Pipelines">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-xs text-text-muted">
            <th className="py-2 text-left font-medium">Pipeline</th>
            <th className="py-2 text-left font-medium">Status</th>
            <th className="py-2 text-left font-medium">Last session</th>
            <th className="py-2 text-right font-medium">Rows last run</th>
            <th className="py-2 text-left font-medium">Freshness</th>
            <th className="py-2 text-left font-medium">Last error</th>
          </tr>
        </thead>
        <tbody>
          {pipelines.map((pipeline) => (
            <tr key={pipeline.name} className="border-b border-border/50">
              <td className="py-2 pr-4 font-medium">{pipeline.name}</td>
              <td className="py-2 pr-4">
                <Badge tone={STATUS_TONE[pipeline.status]}>{STATUS_LABEL[pipeline.status]}</Badge>
              </td>
              <td className="py-2 pr-4 tabular-nums text-text-muted">
                {pipeline.lastSession ?? "—"}
              </td>
              <td className="py-2 pr-4 text-right tabular-nums">
                {pipeline.status === "never" || pipeline.status === "disabled"
                  ? "—"
                  : `${pipeline.rowsLastRun} rows`}
              </td>
              <td className="py-2 pr-4">{freshness(pipeline)}</td>
              <td className="py-2 text-xs text-danger" title={pipeline.error ?? undefined}>
                {truncate(pipeline.error)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

/** Staleness in sessions, because three calendar days over a weekend is one. */
function freshness(pipeline: PipelineHealth): string {
  if (pipeline.staleSessions === null) return "—";
  if (pipeline.staleSessions === 0) return "current";
  return `${pipeline.staleSessions} ${pipeline.staleSessions === 1 ? "session" : "sessions"} behind`;
}

function truncate(error: string | null): string {
  if (!error) return "";
  return error.length > ERROR_CHARS ? `${error.slice(0, ERROR_CHARS)}…` : error;
}
